import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Download, ArrowUpDown, ArrowUp, ArrowDown, Table2 } from "lucide-react";
import api from "@/lib/api";
import { MultiSelect } from "@/components/dashboard/FilterBar";

const COLS = [
  ["model_code", "Model Code"], ["model_name", "Model Name"], ["colour", "Colour"],
  ["chassis_no", "Chassis"], ["purchase_date", "Purchase Date"], ["delivery_date", "Delivery Date"],
  ["delivered_to", "Delivered To"], ["rto_done", "RTO Done"], ["age_days", "Age (days)"],
];
const AGE_OPTS = [["", "Age: Any"], ["30", "Age > 30"], ["60", "Age > 60"], ["90", "Age > 90"], ["120", "Age > 120"], ["180", "Age > 180"]];
const dateCls = "h-9 rounded-md border border-[#334155] bg-[#0B0F17] px-2 text-xs text-slate-300";

export const AllBikesTable = () => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ pFrom: "", pTo: "", dFrom: "", dTo: "", chassis: [], codes: [], names: [], colours: [], delivered: [], ageGt: "" });
  const [sort, setSort] = useState({ key: "purchase_date", dir: "desc" });
  const [limit, setLimit] = useState(100);

  useEffect(() => { api.get("/bikes/all").then((r) => setRows(r.data)).catch(() => {}); }, []);

  const opts = useMemo(() => ({
    chassis: [...new Set(rows.map((r) => r.chassis_no))].sort(),
    codes: [...new Set(rows.map((r) => r.model_code).filter(Boolean))].sort(),
    names: [...new Set(rows.map((r) => r.model_name).filter(Boolean))].sort(),
    colours: [...new Set(rows.map((r) => r.colour).filter(Boolean))].sort(),
  }), [rows]);

  const filtered = useMemo(() => {
    let out = rows.filter((r) =>
      (!f.pFrom || (r.purchase_date && r.purchase_date >= f.pFrom)) &&
      (!f.pTo || (r.purchase_date && r.purchase_date <= f.pTo)) &&
      (!f.dFrom || (r.delivery_date && r.delivery_date >= f.dFrom)) &&
      (!f.dTo || (r.delivery_date && r.delivery_date <= f.dTo)) &&
      (!f.chassis.length || f.chassis.includes(r.chassis_no)) &&
      (!f.codes.length || f.codes.includes(r.model_code)) &&
      (!f.names.length || f.names.includes(r.model_name)) &&
      (!f.colours.length || f.colours.includes(r.colour)) &&
      (!f.delivered.length || f.delivered.includes(r.delivered)) &&
      (!f.ageGt || (r.age_days != null && r.age_days > Number(f.ageGt))));
    const { key, dir } = sort;
    out = [...out].sort((a, b) => {
      const av = a[key], bv = b[key];
      if (av == null || av === "") return 1;
      if (bv == null || bv === "") return -1;
      const c = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return dir === "asc" ? c : -c;
    });
    return out;
  }, [rows, f, sort]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setLimit(100); };

  const downloadXlsx = () => {
    const data = filtered.map((r, i) => ({ "#": i + 1, ...Object.fromEntries(COLS.map(([k, l]) => [l, r[k] ?? ""])) }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "All Bikes");
    XLSX.writeFile(wb, `OPM-All-Bikes-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div data-testid="all-bikes-table" className="bg-[#1E293B] border border-[#334155] rounded-xl p-3.5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-slate-100 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          <Table2 className="h-4 w-4 text-blue-400" /> All Bikes — Complete Detail
          <span className="text-[10px] font-normal text-slate-400">{filtered.length} of {rows.length}</span>
        </h3>
        <Button data-testid="bikes-export-btn" size="sm" onClick={downloadXlsx}
          className="h-8 text-[11px] bg-emerald-600 hover:bg-emerald-500 text-white">
          <Download className="h-3.5 w-3.5 mr-1" /> Excel
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-[10px] text-slate-500 uppercase font-bold">Purchase</span>
        <input data-testid="bikes-pfrom" type="date" value={f.pFrom} onChange={(e) => set("pFrom", e.target.value)} className={dateCls} />
        <input data-testid="bikes-pto" type="date" value={f.pTo} onChange={(e) => set("pTo", e.target.value)} className={dateCls} />
        <span className="text-[10px] text-slate-500 uppercase font-bold">Delivery</span>
        <input data-testid="bikes-dfrom" type="date" value={f.dFrom} onChange={(e) => set("dFrom", e.target.value)} className={dateCls} />
        <input data-testid="bikes-dto" type="date" value={f.dTo} onChange={(e) => set("dTo", e.target.value)} className={dateCls} />
        <MultiSelect testId="bikes-filter-chassis" label="Chassis" options={opts.chassis} selected={f.chassis} onChange={(v) => set("chassis", v)} />
        <MultiSelect testId="bikes-filter-code" label="Model Code" options={opts.codes} selected={f.codes} onChange={(v) => set("codes", v)} />
        <MultiSelect testId="bikes-filter-name" label="Model Name" options={opts.names} selected={f.names} onChange={(v) => set("names", v)} />
        <MultiSelect testId="bikes-filter-colour" label="Colour" options={opts.colours} selected={f.colours} onChange={(v) => set("colours", v)} />
        <MultiSelect testId="bikes-filter-delivered" label="Delivered" options={["Yes", "No"]} selected={f.delivered} onChange={(v) => set("delivered", v)} />
        <select data-testid="bikes-filter-age" value={f.ageGt} onChange={(e) => set("ageGt", e.target.value)}
          className={`${dateCls} ${f.ageGt ? "text-blue-400 border-blue-500/50" : ""}`}>
          {AGE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button data-testid="bikes-filter-reset" onClick={() => setF({ pFrom: "", pTo: "", dFrom: "", dTo: "", chassis: [], codes: [], names: [], colours: [], delivered: [], ageGt: "" })}
          className="text-[11px] text-slate-400 hover:text-white underline underline-offset-2">Reset</button>
        <span data-testid="bikes-count-inline" className="ml-auto rounded-full bg-blue-500/15 border border-blue-500/40 text-blue-400 text-[11px] font-bold px-3 py-1.5 whitespace-nowrap">
          Total: {filtered.length.toLocaleString("en-IN")}{filtered.length !== rows.length && ` / ${rows.length.toLocaleString("en-IN")}`}
        </span>
      </div>
      <div className="overflow-x-auto max-h-[480px] overflow-y-auto rounded-lg border border-[#334155]">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-[#111827] z-10">
            <tr>
              <th className="px-2.5 py-2 text-left font-bold text-slate-400 uppercase text-[9px] tracking-wide whitespace-nowrap">#</th>
              {COLS.map(([k, l]) => (
                <th key={k} data-testid={`bikes-sort-${k}`} onClick={() => toggleSort(k)}
                  className="px-2.5 py-2 text-left font-bold text-slate-400 uppercase text-[9px] tracking-wide cursor-pointer hover:text-white whitespace-nowrap select-none">
                  <span className="flex items-center gap-1">{l}
                    {sort.key === k ? (sort.dir === "asc" ? <ArrowUp className="h-3 w-3 text-blue-400" /> : <ArrowDown className="h-3 w-3 text-blue-400" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {filtered.slice(0, limit).map((r, idx) => (
              <tr key={r.chassis_no} className="hover:bg-slate-800/40">
                <td className="px-2.5 py-1.5 text-slate-500 font-mono whitespace-nowrap">{idx + 1}</td>
                <td className="px-2.5 py-1.5 text-slate-300 whitespace-nowrap">{r.model_code}</td>
                <td className="px-2.5 py-1.5 text-slate-200 whitespace-nowrap">{r.model_name}</td>
                <td className="px-2.5 py-1.5 text-slate-400 whitespace-nowrap">{r.colour}</td>
                <td className="px-2.5 py-1.5 font-mono text-orange-400 whitespace-nowrap">{r.chassis_no}</td>
                <td className="px-2.5 py-1.5 text-slate-300 whitespace-nowrap">{r.purchase_date}</td>
                <td className="px-2.5 py-1.5 text-slate-300 whitespace-nowrap">{r.delivery_date || "—"}</td>
                <td className="px-2.5 py-1.5 text-slate-400 whitespace-nowrap max-w-[180px] truncate">{r.delivered_to || "—"}</td>
                <td className="px-2.5 py-1.5 whitespace-nowrap">{r.rto_done ? <span className={r.rto_done === "Yes" ? "text-emerald-400" : "text-rose-400"}>{r.rto_done}</span> : "—"}</td>
                <td className={`px-2.5 py-1.5 font-mono whitespace-nowrap ${r.age_days > 90 ? "text-rose-400" : r.age_days > 60 ? "text-amber-400" : "text-slate-300"}`}>{r.age_days ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length > limit && (
          <button data-testid="bikes-show-more" onClick={() => setLimit((l) => l + 200)}
            className="w-full py-2 text-[11px] text-blue-400 hover:bg-slate-800/50">Show more ({filtered.length - limit} remaining)</button>
        )}
        {!filtered.length && <p className="text-center text-xs text-slate-500 py-8">No bikes match the filters.</p>}
      </div>
    </div>
  );
};
